import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import LocationBanner from '../components/LocationBanner';

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

const DAYS = [
  { key: 'mon', label: 'Bazar ertəsi' },
  { key: 'tue', label: 'Çərşənbə axşamı' },
  { key: 'wed', label: 'Çərşənbə' },
  { key: 'thu', label: 'Cümə axşamı' },
  { key: 'fri', label: 'Cümə' },
  { key: 'sat', label: 'Şənbə' },
  { key: 'sun', label: 'Bazar' },
];

const DEFAULT_SCHEDULE = DAYS.map(d => ({
  day: d.key,
  open: true,
  from: '09:00',
  to: '22:00',
}));

const parseStored = (raw) => {
  if (!raw) return { schedule: DEFAULT_SCHEDULE, closedDates: [] };
  try {
    const parsed = JSON.parse(raw);
    if (parsed.schedule) return parsed;
    return { schedule: DEFAULT_SCHEDULE, closedDates: [] };
  } catch {
    return { schedule: DEFAULT_SCHEDULE, closedDates: [] };
  }
};

const card = {
  background: '#fff',
  borderRadius: 16,
  padding: '24px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  marginBottom: 24,
};

export default function HoursPage() {
  const { business } = useAuth();
  const { activeLocation, locationId } = useLocation();
  const winW = useWindowWidth();
  const isMobile = winW < 600;

  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [closedDates, setClosedDates] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [bulkFrom, setBulkFrom] = useState('09:00');
  const [bulkTo, setBulkTo] = useState('22:00');

  useEffect(() => {
    const src = activeLocation || business;
    const { schedule: s, closedDates: cd } = parseStored(src?.working_hours);
    setSchedule(s);
    setClosedDates(cd);
  }, [activeLocation, business]);

  const updateDay = (dayKey, field, value) => {
    setSchedule(prev => prev.map(d => d.day === dayKey ? { ...d, [field]: value } : d));
  };

  const toggleDay = (dayKey) => {
    setSchedule(prev => prev.map(d => d.day === dayKey ? { ...d, open: !d.open } : d));
  };

  const setAll = (from, to) => {
    setSchedule(prev => prev.map(d => d.open ? { ...d, from, to } : d));
  };

  const addClosedDate = () => {
    if (!newDate || closedDates.includes(newDate)) return;
    setClosedDates(prev => [...prev, newDate].sort());
    setNewDate('');
  };

  const removeClosedDate = (date) => {
    setClosedDates(prev => prev.filter(d => d !== date));
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    const payload = JSON.stringify({ schedule, closedDates });
    try {
      let error;
      if (locationId) {
        ({ error } = await supabase
          .from('business_locations')
          .update({ working_hours: payload })
          .eq('id', locationId));
      } else {
        ({ error } = await supabase
          .from('businesses')
          .update({ working_hours: payload })
          .eq('id', business.id));
      }
      if (error) throw error;
      setMsg('✅ Uğurla saxlanıldı!');
    } catch (e) {
      console.error(e);
      setMsg('❌ Xəta baş verdi.');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '16px 12px 60px' : '24px 20px 60px' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>🕐 İş Saatları & Bağlı Günlər</h1>
        <p style={{ color: '#888', marginTop: 6, fontSize: 13 }}>Hər gün üçün iş saatlarını təyin edin</p>
      </div>

      <LocationBanner />

      {/* Quick-set row */}
      <div style={{ ...card, padding: isMobile ? '14px' : '18px 20px', marginBottom: isMobile ? 14 : 24 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 10 }}>
          Bütün günlərə tətbiq et
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <TimeSelect value={bulkFrom} onChange={setBulkFrom} />
          <span style={{ color: '#aaa', fontWeight: 700 }}>–</span>
          <TimeSelect value={bulkTo} onChange={setBulkTo} />
          <button
            onClick={() => setAll(bulkFrom, bulkTo)}
            style={{
              padding: '8px 16px', borderRadius: 10, border: 'none',
              background: '#f97316', color: '#fff',
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >Tətbiq et</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <span style={{ fontSize: 12, color: '#aaa' }}>Tez seçim:</span>
          {[
            { label: '09–22', from: '09:00', to: '22:00' },
            { label: '08–23', from: '08:00', to: '23:00' },
            { label: '10–21', from: '10:00', to: '21:00' },
            { label: '24 saat', from: '00:00', to: '23:59' },
          ].map(p => (
            <button
              key={p.label}
              onClick={() => { setBulkFrom(p.from); setBulkTo(p.to); setAll(p.from, p.to); }}
              style={{
                padding: '5px 12px', borderRadius: 20, border: '1px solid #e0e0e0',
                background: '#f9f9f9', color: '#444', fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
              }}
            >{p.label}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#bbb', marginTop: 8 }}>Yalnız açıq günlərə tətbiq olunur</div>
      </div>

      {/* Schedule */}
      <div style={{ ...card, padding: isMobile ? '16px 12px' : '24px', marginBottom: isMobile ? 14 : 24 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 14, marginTop: 0 }}>Həftəlik İş Saatları</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DAYS.map(({ key, label }) => {
            const row = schedule.find(s => s.day === key) || { open: true, from: '09:00', to: '22:00' };
            return (
              <div key={key} style={{
                padding: isMobile ? '10px 12px' : '12px 16px',
                borderRadius: 12,
                background: row.open ? '#fff' : '#f8f8f8',
                border: `1px solid ${row.open ? '#e8e8e8' : '#efefef'}`,
              }}>
                {/* Top row: toggle + day name + (desktop: times) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Toggle */}
                  <div onClick={() => toggleDay(key)} style={{
                    width: 40, height: 22, borderRadius: 11,
                    background: row.open ? '#f97316' : '#d0d0d0',
                    position: 'relative', cursor: 'pointer',
                    transition: 'background 0.2s', flexShrink: 0,
                  }}>
                    <div style={{
                      position: 'absolute', top: 2, left: row.open ? 20 : 2,
                      width: 18, height: 18, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    }} />
                  </div>

                  {/* Day name */}
                  <span style={{
                    fontSize: isMobile ? 13 : 14, fontWeight: 600,
                    color: row.open ? '#1a1a2e' : '#bbb',
                    flex: 1,
                  }}>{label}</span>

                  {/* Desktop: times inline */}
                  {!isMobile && (row.open ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <TimeSelect value={row.from} onChange={v => updateDay(key, 'from', v)} />
                      <span style={{ color: '#aaa', fontSize: 13 }}>–</span>
                      <TimeSelect value={row.to} onChange={v => updateDay(key, 'to', v)} />
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: '#bbb', fontWeight: 600 }}>Bağlı</span>
                  ))}

                  {/* Mobile: closed label */}
                  {isMobile && !row.open && (
                    <span style={{ fontSize: 12, color: '#bbb', fontWeight: 600 }}>Bağlı</span>
                  )}
                </div>

                {/* Mobile: time inputs on second line */}
                {isMobile && row.open && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, paddingLeft: 50 }}>
                    <TimeSelect value={row.from} onChange={v => updateDay(key, 'from', v)} />
                    <span style={{ color: '#aaa', fontSize: 13 }}>–</span>
                    <TimeSelect value={row.to} onChange={v => updateDay(key, 'to', v)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Closed Dates */}
      <div style={{ ...card, padding: isMobile ? '16px 12px' : '24px', marginBottom: isMobile ? 14 : 24 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 4, marginTop: 0 }}>📅 Bağlı Günlər (Tətil / Bayram)</h2>
        <p style={{ fontSize: 12, color: '#999', marginBottom: 14, marginTop: 0 }}>
          Rezervasiyalar bu günlər üçün avtomatik bağlanacaq
        </p>

        {/* Add date */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            type="date"
            min={today}
            value={newDate}
            onChange={e => setNewDate(e.target.value)}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 10,
              border: '1px solid #e0e0e0', fontSize: 14,
              outline: 'none', color: '#333',
            }}
          />
          <button
            onClick={addClosedDate}
            disabled={!newDate}
            style={{
              padding: '10px 16px', borderRadius: 10, border: 'none',
              background: newDate ? '#f97316' : '#eee',
              color: newDate ? '#fff' : '#aaa',
              fontWeight: 700, fontSize: 14, cursor: newDate ? 'pointer' : 'default',
              flexShrink: 0,
            }}
          >{isMobile ? '+' : '+ Əlavə et'}</button>
        </div>

        {/* Date list */}
        {closedDates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#ccc', fontSize: 14 }}>
            Bağlı gün yoxdur
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {closedDates.map(date => {
              const d = new Date(date + 'T00:00:00');
              const fmt = d.toLocaleDateString('az-AZ', { day: '2-digit', month: 'long', year: 'numeric' });
              const isPast = date < today;
              return (
                <div key={date} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '7px 14px', borderRadius: 20,
                  background: isPast ? '#f5f5f5' : '#fff7ed',
                  border: `1px solid ${isPast ? '#e0e0e0' : '#fed7aa'}`,
                  fontSize: 13, fontWeight: 600,
                  color: isPast ? '#aaa' : '#c2410c',
                }}>
                  {fmt}
                  <button
                    onClick={() => removeClosedDate(date)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 0, color: 'inherit', fontSize: 14, lineHeight: 1,
                      opacity: 0.6,
                    }}
                  >✕</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Save */}
      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 12,
          background: msg.includes('✅') ? '#f0fdf4' : '#fef2f2',
          color: msg.includes('✅') ? '#16a34a' : '#dc2626',
          fontWeight: 600, fontSize: 14,
        }}>{msg}</div>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          width: '100%', padding: isMobile ? '13px' : '14px', borderRadius: 12, border: 'none',
          background: saving ? '#fed7aa' : '#f97316',
          color: '#fff', fontWeight: 800, fontSize: 15,
          cursor: saving ? 'default' : 'pointer',
          boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
          position: isMobile ? 'sticky' : 'static',
          bottom: isMobile ? 16 : 'auto',
        }}
      >
        {saving ? 'Saxlanılır...' : '💾 Yadda Saxla'}
      </button>
    </div>
  );
}

const timeInputStyle = {
  padding: '7px 10px', borderRadius: 8,
  border: '1px solid #e0e0e0', fontSize: 14,
  fontWeight: 600, color: '#333', outline: 'none',
  cursor: 'pointer',
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINS  = ['00', '15', '30', '45'];

function TimeSelect({ value, onChange, style = {} }) {
  const [h, m] = (value || '00:00').split(':');
  const selStyle = {
    padding: '6px 4px', borderRadius: 8,
    border: '1px solid #e0e0e0', fontSize: 14,
    fontWeight: 700, color: '#333', background: '#fff',
    cursor: 'pointer', outline: 'none',
  };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, ...style }}>
      <select value={h} onChange={e => onChange(`${e.target.value}:${m}`)} style={selStyle}>
        {HOURS.map(hh => <option key={hh} value={hh}>{hh}</option>)}
      </select>
      <span style={{ fontWeight: 800, color: '#666', padding: '0 1px' }}>:</span>
      <select value={m || '00'} onChange={e => onChange(`${h}:${e.target.value}`)} style={selStyle}>
        {MINS.map(mm => <option key={mm} value={mm}>{mm}</option>)}
      </select>
    </div>
  );
}
